import React from 'react';
import { Link } from 'react-router-dom';

// Updated plan data
const plans = [
  {
    id: 'basic',
    name: '🟦 Basic Plan',
    price: '$99/month',
    tagline: "Effortless Upkeep for Your Website",
    features: [
      { icon: '✅', title: 'Site maintenance, updates, backups, and monitoring', description: '' },
      { icon: '🔍', title: 'Basic SEO tune-ups', description: 'keywords, metadata, structure' },
      { icon: '📊', title: 'Monthly traffic reports with quick improvement tips', description: '' }
    ],
    idealFor: "Ideal for Shopify owners or anyone needing consistent site care. Best for solopreneurs and hands-off business owners.",
    lookup_key: process.env.REACT_APP_BASIC,
    mode: 'subscription'
  },
  {
    id: 'premium',
    name: '🟪 Premium Plan',
    price: '$299/month',
    tagline: "Your On-Demand Web Team",
    features: [
      { icon: '🚀', title: 'Faster fixes, updates, and turnaround times', description: '' },
      { icon: '🔧', title: 'Up to 10 monthly design or tech requests via email', description: '' },
      { icon: '🔐', title: 'Early access to AISS tools like our Social Media Manager', description: '' },
      { icon: '🧠', title: 'Custom monthly insights to boost site speed and UX', description: '' }
    ],
    idealFor: "Everything in Basic plus priority support and microservice access. Built for growing brands that need reliable, expert support.",
    lookup_key: process.env.REACT_APP_PREMIUM,
    mode: 'subscription'
  },
  {
    id: 'onetime',
    name: 'One-Time Build – "Foundations"',
    price: '$899 flat',
    tagline: 'A solid start for your online presence.', // Added a tagline for consistency
    features: [ // Kept original features as strings for this plan, can be updated to object structure if needed
      { icon: '🛠️', title: 'Full-featured custom site', description: ''},
      { icon: '⚙️', title: 'Core SEO & Analytics', description: ''},
      { icon: '📱', title: 'Mobile optimization', description: ''},
      { icon: '🎨', title: 'Branding assets', description: ''},
      { icon: '🤝', title: 'Handoff & domain integration', description: ''}
    ],
    idealFor: 'Those needing a foundational website built right.',
    lookup_key: process.env.REACT_APP_FOUNDATIONS_ONETIME_PRICE_ID,
    mode: 'payment'
  }
];

// Backend is served from the same origin as the front-end on Replit.
// Allow an override via REACT_APP_API_BASE_URL for future deployments.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const pageStyle = {
  padding: '50px 20px',
  backgroundColor: '#0A0F1A', // Slightly darker, richer blue
  color: '#E2E8F0',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif' // Modern sans-serif font
};

const cardStyle = {
  border: '1px solid #334155', // Slate border
  borderRadius: '12px', // Softer rounding
  padding: '30px',
  margin: '15px',
  width: '360px', // Slightly wider for more content
  backgroundColor: '#1E293B', // Darker slate card background
  color: '#CBD5E1', // Lighter text for contrast
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.2), 0 20px 20px -10px rgba(0, 0, 0, 0.1)', // More pronounced shadow
  transition: 'transform 0.3s ease, box-shadow 0.3s ease' // Hover effect
};

const cardHoverStyle = {
  transform: 'translateY(-5px)',
  boxShadow: '0 15px 30px -5px rgba(0, 0, 0, 0.25), 0 25px 25px -10px rgba(0, 0, 0, 0.15)'
};


const buttonStyle = {
  backgroundColor: '#3B82F6', // Brighter blue for CTA
  color: 'white',
  padding: '12px 20px',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  marginTop: '25px',
  fontSize: '1.05rem',
  fontWeight: '600', // Bolder button text
  width: '100%',
  transition: 'background-color 0.3s ease'
};

const buttonHoverStyle = {
  backgroundColor: '#2563EB' // Darker blue on hover
};

const backButtonStyle = {
  display: 'inline-block',
  padding: '10px 20px',
  marginBottom: '40px', // More space
  backgroundColor: '#4A5568',
  color: 'white',
  textDecoration: 'none',
  borderRadius: '8px',
  fontSize: '1rem',
  transition: 'background-color 0.3s ease',
  alignSelf: 'flex-start'
};

const titleStyle = {
  textAlign: 'center',
  marginBottom: '40px', // More space
  fontSize: '2.8rem', // Larger title
  fontWeight: 'bold',
  color: '#F1F5F9' // Brighter title color
};

const cardsContainerStyle = {
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: '30px' // Increased gap
};

const planNameStyle = {
  color: '#93C5FD', // Light blue accent for plan name
  fontSize: '1.75rem', // Larger plan name
  fontWeight: 'bold',
  marginBottom: '8px'
};

const planPriceStyle = {
  fontSize: '1.5rem', // Prominent price
  fontWeight: '600',
  color: '#F1F5F9',
  marginBottom: '8px'
};

const planTaglineStyle = {
  fontSize: '1rem',
  color: '#94A3B8', // Softer color for tagline
  marginBottom: '20px',
  fontStyle: 'italic',
  minHeight: '40px' // Ensure consistent height for taglines
};

const featuresHeadingStyle = {
  fontSize: '1.1rem',
  fontWeight: '600',
  color: '#E2E8F0',
  marginTop: '20px',
  marginBottom: '12px'
};

const featureItemStyle = {
  display: 'flex',
  alignItems: 'flex-start', // Align icon with first line of text
  marginBottom: '12px',
  fontSize: '0.95rem',
  lineHeight: '1.6'
};

const featureIconStyle = {
  marginRight: '10px',
  fontSize: '1.2rem', // Slightly larger icon
  color: '#3B82F6' // Use button color for icons
};

const featureTitleStyle = {
  fontWeight: '600',
  color: '#CBD5E1'
};

const featureDescriptionStyle = {
  color: '#94A3B8', // Softer color for description
  marginLeft: '26px', // Indent description under title if icon + title are on one line
  fontSize: '0.9rem'
};

const idealForStyle = {
  fontSize: '0.9rem',
  color: '#94A3B8',
  marginTop: 'auto', // Pushes to bottom if card content varies
  paddingTop: '20px', // Space before button if idealFor is short
  minHeight: '60px' // Consistent height for idealFor section
};


function SubscriptionPage() {
  const handleSubscription = async (lookupKey, mode) => {
    console.log('Attempting checkout for Lookup Key:', lookupKey, 'Mode:', mode);
    try {
      const response = await fetch(`${API_BASE_URL}/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ lookupKey: lookupKey, mode: mode }),
      });

      let responseText = '';
      try {
        responseText = await response.text();
        const session = JSON.parse(responseText);

        if (response.ok) {
          if (session.url) {
            window.location.href = session.url;
          } else {
            console.error('Stripe session URL not found in parsed JSON:', session);
            alert('Error: Could not retrieve checkout session URL from parsed JSON.');
          }
        } else {
          console.error('Server error creating session (parsed JSON):', session, 'Status:', response.status, 'Raw Response:', responseText);
          alert(session.error || `Failed to create checkout session. Status: ${response.status}. Check server logs.`);
        }
      } catch (jsonParseError) {
        console.error('Failed to parse server response as JSON. Status:', response.status, 'Raw Response Text:', responseText, 'Parse Error:', jsonParseError);
        alert(`Error processing server response. Raw response might not be JSON. Please check console. Status: ${response.status}`);
      }
    } catch (fetchError) {
      console.error('Client-side error during fetch operation:', fetchError);
      alert(`Network or client error: ${fetchError.message}. Unable to contact server.`);
    }
  };
  
  // State for hover effects
  const [hoveredCard, setHoveredCard] = React.useState(null);
  const [hoveredButton, setHoveredButton] = React.useState(null);
  const [hoveredBackButton, setHoveredBackButton] = React.useState(false);

  return (
    <div style={pageStyle}>
      <Link
        to="/"
        style={{ ...backButtonStyle, ...(hoveredBackButton ? buttonHoverStyle : {}) }}
        onMouseOver={() => setHoveredBackButton(true)}
        onMouseOut={() => setHoveredBackButton(false)}
      >
        &larr; Back to Home
      </Link>
      <h1 style={titleStyle}>Choose Your Plan</h1>
      
      <div style={cardsContainerStyle}>
        {plans.map(plan => (
          <div 
            key={plan.id} 
            style={{ ...cardStyle, ...(hoveredCard === plan.id ? cardHoverStyle : {}) }}
            onMouseOver={() => setHoveredCard(plan.id)}
            onMouseOut={() => setHoveredCard(null)}
          >
            <div>
              <h3 style={planNameStyle}>{plan.name}</h3>
              <p style={planPriceStyle}>{plan.price}</p>
              {plan.tagline && <p style={planTaglineStyle}>{plan.tagline}</p>}
              
              <h4 style={featuresHeadingStyle}>Includes:</h4>
              <ul style={{ listStyleType: 'none', padding: 0, marginBottom: '20px' }}>
                {plan.features.map((feature, index) => (
                  <li key={index} style={featureItemStyle}>
                    <span style={featureIconStyle}>{feature.icon}</span>
                    <div>
                      <span style={featureTitleStyle}>{feature.title}</span>
                      {feature.description && <p style={featureDescriptionStyle}>{feature.description}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              {plan.idealFor && <p style={idealForStyle}>{plan.idealFor}</p>}
              <button
                style={{ ...buttonStyle, ...(hoveredButton === plan.id ? buttonHoverStyle : {}) }}
                onClick={() => handleSubscription(plan.lookup_key, plan.mode)}
                onMouseOver={() => setHoveredButton(plan.id)}
                onMouseOut={() => setHoveredButton(null)}
              >
                {plan.mode === 'subscription' ? 'Subscribe Now' : 'Purchase Now'}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default SubscriptionPage; 